import { Map, fromJS } from "immutable";

import {
    CONNECTION_REQUESTS_FAILURE,
    CONNECTION_REQUESTS_FILTER_CHANGE,
    CONNECTION_REQUESTS_REQUEST_START,
    CONNECTION_REQUESTS_RESULT,
    CONNECTION_REQUESTS_SORT_CHANGE,
    CONNECTION_REQUESTS_SEARCH_CHANGE,
    CONNECTION_REQUESTS_FOR_BUYER,
    CONNECTION_REQUEST_EDIT_START,
    CONNECTION_REQUEST_EDIT_SUCCESS,
    CONNECTION_REQUEST_EDIT_ERROR,
    CONNECTION_REQUESTS_COUNT,
    CONNECTION_REQUESTS_RETAILER_COMPANIES_START,
    CONNECTION_REQUESTS_RETAILER_COMPANIES_SUCCESS,
    CONNECTION_REQUESTS_RETAILER_COMPANIES_FAILURE,
} from "constants/action-types";

import { ConnectionRequestEvents } from "constants/events";
import * as queries from "queries/connection-requests";
import { requestAsync, mutateAsync } from "lib/api";
import { trackChurnZeroEvent } from "lib/churn-zero";
import Globalize from "globalize";
import { add as snackbarAdd } from "actions/snackbars";
import _ from "lodash";
import { checkStatus } from "actions/base-actions";

export type ConnectionRequestUpdateTypes =
    | "approve"
    | "deny"
    | "block"
    | "unblock"
    | "archive"
    | "unarchive"
    | "review";

export function updateStatus(id: string, action: ConnectionRequestUpdateTypes, data?, message?) {
    return (dispatch, getState) => {
        dispatch({ type: CONNECTION_REQUEST_EDIT_START });

        const brand = getState().scope.brand;
        dispatch(mutateAsync(queries.updateStatus(id, brand._id, action, data))).then((response) => {
            const { status, body } = response;
            trackChurnZeroEvent({
                name: ConnectionRequestEvents.StatusUpdated,
                customFields: {
                    connectionRequestId: body._id,
                    brandId: body.brand_id,
                    companyId: body.company_id,
                    buyerId: body.user_id,
                    retailerId: body.retailer_id,
                    status: body.status,
                    statusUpdatedOn: body.status_updated_on,
                },
            });
            if (status >= 200 && status < 300) {
                dispatch({ type: CONNECTION_REQUEST_EDIT_SUCCESS, payload: { id, action, status: body.status } });
                dispatch(getPendingCount());
                switch (action) {
                    case "approve":
                        dispatch(snackbarAdd({ message }));
                        break;
                    case "deny":
                        dispatch(snackbarAdd({ message: Globalize.formatMessage("Connection request denied") }));
                        break;
                    case "block":
                        dispatch(snackbarAdd({ message: Globalize.formatMessage("Connection request blocked") }));
                        break;
                    case "unblock":
                        dispatch(snackbarAdd({ message: Globalize.formatMessage("Connection request unblocked") }));
                        break;
                    case "archive":
                        dispatch(
                            snackbarAdd({
                                message: data?.b2bPhase1
                                    ? Globalize.formatMessage("Buyer added to contacts and request moved to archived.")
                                    : Globalize.formatMessage("Request was archived."),
                                options: data?.b2bPhase1
                                    ? {
                                          open: true,
                                          b2bPhase1: true,
                                      }
                                    : {},
                            })
                        );
                        break;
                    case "unarchive":
                        dispatch(snackbarAdd({ message: Globalize.formatMessage("Request was unarchived") }));
                        break;
                    case "review":
                        dispatch(
                            snackbarAdd({ message: Globalize.formatMessage("Request status moved to In review.") })
                        );
                        break;
                }
            } else {
                dispatch({ type: CONNECTION_REQUEST_EDIT_ERROR });
                dispatch(
                    snackbarAdd({
                        message: Globalize.formatMessage("Oops, there was a problem! Please refresh and try again."),
                    })
                );
            }
        });
    };
}

export const fetchNextPage = () => {
    return (dispatch, getState) => {
        const { nextPage, isFetching } = getState().connectionRequests;
        if (nextPage && !isFetching) {
            dispatch({
                type: CONNECTION_REQUESTS_REQUEST_START,
                payload: {},
            });

            dispatch(requestAsync(queries.getNextQuery(nextPage))).then(({ status, transformed, headers }) => {
                if (status >= 200 && status < 300) {
                    dispatch({
                        type: CONNECTION_REQUESTS_RESULT,
                        payload: transformed._result,
                        meta: {
                            next: headers.next,
                            replace: false,
                        },
                    });
                } else {
                    dispatch({
                        type: CONNECTION_REQUESTS_FAILURE,
                        payload: new Error(
                            Globalize.formatMessage("Oops, there was a problem! Please refresh and try again.")
                        ),
                        error: true,
                    });
                    dispatch(
                        snackbarAdd({
                            message: Globalize.formatMessage(
                                "Oops, there was a problem! Please refresh and try again."
                            ),
                        })
                    );
                }
            });
        }
    };
};

export const startFetchPage = (reset) => {
    return {
        type: CONNECTION_REQUESTS_REQUEST_START,
        payload: {
            reset,
        },
    };
};

export const connectionsResult = ({ result, next, replace = true, status }) => {
    return (dispatch, getState) => {
        if (status >= 200 && status < 300) {
            dispatch({
                type: CONNECTION_REQUESTS_RESULT,
                payload: result ? result._result : [],
                meta: {
                    replace,
                    next,
                },
            });
        } else {
            dispatch({
                type: CONNECTION_REQUESTS_RESULT,
                payload: new Error(Globalize.formatMessage("Oops, there was a problem! Please refresh and try again.")),
                error: true,
            });
            dispatch(
                snackbarAdd({
                    message: Globalize.formatMessage("Oops, there was a problem! Please refresh and try again."),
                })
            );
        }
    };
};

const _setSearch = (search) => ({
    type: CONNECTION_REQUESTS_SEARCH_CHANGE,
    payload: {
        search,
    },
});

export const searchChange = (search) => {
    return (dispatch, getState) => {
        dispatch(_setSearch(search));
        dispatch(startFetchPage(true));

        const scope = getState().scope;
        const requestsState = _.assign({ scope }, getState().connectionRequests);
        dispatch(requestAsync(queries.getQuery(requestsState))).then((data) => {
            const { status, transformed, body, headers } = data;
            if (status >= 200 && status < 300) {
                dispatch(connectionsResult({ result: transformed, next: headers.next, status }));
            } else {
                dispatch({
                    type: CONNECTION_REQUESTS_FAILURE,
                    payload:
                        body && body.message
                            ? new Error(body.message)
                            : new Error(
                                  Globalize.formatMessage("Oops, there was a problem! Please refresh and try again.")
                              ),
                    error: true,
                });

                dispatch(
                    snackbarAdd({
                        message: Globalize.formatMessage("Oops, there was a problem! Please refresh and try again."),
                    })
                );
            }
        });
    };
};

export const init = ({ filters, sort, sortOrder, search }) => {
    return (dispatch) => {
        dispatch(setFilters(filters));
        dispatch(_setSearch(search));
        dispatch(_setSort(sort, sortOrder));
    };
};

export const setFilters = (filters) => {
    const isMap = Map.isMap(filters);
    return {
        type: CONNECTION_REQUESTS_FILTER_CHANGE,
        payload: {
            filters: isMap ? filters : fromJS(filters),
        },
    };
};

const _setSort = (sortBy, sortOrder) => ({
    type: CONNECTION_REQUESTS_SORT_CHANGE,
    payload: {
        __sort: sortBy,
        __sort_order: sortOrder,
    },
});

export const setSort = (sortBy, sortOrder) => {
    return (dispatch, getState) => {
        dispatch(_setSort(sortBy, sortOrder));
        dispatch(startFetchPage(true));
        const scope = getState().scope;
        const requestsState = _.assign({ scope }, getState().connectionRequests);
        dispatch(requestAsync(queries.getQuery(requestsState))).then(({ status, transformed, body, headers }) => {
            if (status >= 200 && status < 300) {
                dispatch(connectionsResult({ result: transformed, next: headers.next, status }));
            } else {
                dispatch({
                    type: CONNECTION_REQUESTS_FAILURE,
                    payload:
                        body && body.message
                            ? new Error(body.message)
                            : new Error(
                                  Globalize.formatMessage("Oops, there was a problem! Please refresh and try again.")
                              ),
                    error: true,
                });
                dispatch(
                    snackbarAdd({
                        message: Globalize.formatMessage("Oops, there was a problem! Please refresh and try again."),
                    })
                );
            }
        });
    };
};

export const fetchRequestedConnections = () => {
    return (dispatch) => {
        return dispatch(requestAsync(queries.getUserConnectionRequestsQuery())).then(({ status, body }) => {
            if (checkStatus(dispatch, status, body)) {
                return dispatch({ type: CONNECTION_REQUESTS_FOR_BUYER, payload: body });
            } else {
                return dispatch({ type: CONNECTION_REQUESTS_FOR_BUYER, error: body });
            }
        });
    };
};

export const getPendingCount = () => {
    return (dispatch, getState) => {
        const brand = getState().scope.brand._id;
        return dispatch(requestAsync(queries.pendingCount(brand))).then(({ status, body }) => {
            if (checkStatus(dispatch, status, body)) {
                dispatch({ type: CONNECTION_REQUESTS_COUNT, payload: body });
            }
        });
    };
};

export const getRetailerCompanies = (retailerId: string, buyers: string[]) => {
    return (dispatch, getState) => {
        dispatch({ type: CONNECTION_REQUESTS_RETAILER_COMPANIES_START });

        return dispatch(requestAsync(queries.retailerCompanies(retailerId, buyers))).then(({ status, body }) => {
            if (status >= 200 && status < 300) {
                dispatch({
                    payload: body,
                    type: CONNECTION_REQUESTS_RETAILER_COMPANIES_SUCCESS,
                });
            } else {
                dispatch({ type: CONNECTION_REQUESTS_RETAILER_COMPANIES_FAILURE });
                dispatch(
                    snackbarAdd({
                        message: Globalize.formatMessage("Oops, there was a problem! Please refresh and try again."),
                    })
                );
            }
        });
    };
};
